import React, { useState, useEffect, useRef, ChangeEvent } from 'react';
import { Label } from '../../../components/ui/label';
import { FormInput } from '../../../components/ui/form-input';
import { FormTextarea } from '../../../components/ui/form-textarea';
import { FormSelect } from '../../../components/ui/form-select';
import { Switch } from '../../../../components/ui/switch';

interface EmailConfiguration {
  enableCustomEmail: boolean;
  replyToAddress: string;
  subject: string;
  customMessage: string;
}

const emailVariables = [
  { value: '{{name}}', label: 'Donor Name' },
  { value: '{{total}}', label: 'Donated Amount' },
  { value: '{{fund}}', label: 'Donated Fund' },
  { value: '{{nonprofit}}', label: 'Nonprofit Name' },
  { value: '{{phone}}', label: 'Donor Phone Number' },
  { value: '{{address}}', label: 'Donor Address' },
  { value: '{{email}}', label: 'Donor Email' },
  { value: '{{paymentMethod}}', label: 'Donor Payment Method' },
  { value: '{{feeCovered}}', label: 'Covered Fee' },
  { value: '{{ein}}', label: 'EIN' },
];

interface EmailConfigurationTabProps {
  emailConfig: EmailConfiguration;
  setEmailConfig: (config: EmailConfiguration) => void;
  handleFieldFocus?: (fieldName: string) => void;
  isFieldInvalid?: (fieldName: string) => boolean;
  setInvalidFields?: (update: (prev: Set<string>) => Set<string>) => void;
  showSectionHeader?: boolean;
}

function EmailConfigurationTab(props: EmailConfigurationTabProps) {
  const { emailConfig, setEmailConfig, handleFieldFocus, isFieldInvalid, setInvalidFields } = props;
  const [hasInitialized, setHasInitialized] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cursorPosRef = useRef<number | null>(null);

  const handleTextareaBlur = () => {
    if (textareaRef.current) {
      cursorPosRef.current = textareaRef.current.selectionStart;
    }
  };

  const handleVariableSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    const variable = e.target.value;
    if (variable) {
      const msg = emailConfig.customMessage || '';
      const pos = cursorPosRef.current ?? msg.length;
      const newValue = msg.slice(0, pos) + variable + msg.slice(pos);
      setEmailConfig({ ...emailConfig, customMessage: newValue });

      const newCursorPos = pos + variable.length;
      cursorPosRef.current = newCursorPos;
      requestAnimationFrame(() => {
        const ta = textareaRef.current;
        if (ta) {
          const scrollTop = ta.scrollTop;
          ta.focus({ preventScroll: true });
          ta.scrollTop = scrollTop;
          ta.setSelectionRange(newCursorPos, newCursorPos);
        }
      });

      e.target.value = '';
    }
  };

  // Add default configuration on first render
  useEffect(() => {
    if (!hasInitialized && Object.keys(emailConfig).length === 0) {
      setEmailConfig({
        enableCustomEmail: false,
        replyToAddress: '',
        subject: '',
        customMessage: '',
      });
      setHasInitialized(true);
    }
  }, [hasInitialized, emailConfig, setEmailConfig]);

  const handleToggleChange = (checked: boolean) => {
    setEmailConfig({
      ...emailConfig,
      enableCustomEmail: checked,
    });

    // If toggle is turned off, clear all email validation errors
    if (!checked && setInvalidFields) {
      setInvalidFields((prev) => {
        const newInvalidFields = new Set(prev);

        // Remove all email-related validation errors
        Array.from(newInvalidFields).forEach((field) => {
          if (field.startsWith('emails-email-')) {
            newInvalidFields.delete(field);
          }
        });

        return newInvalidFields;
      });
    }
  };

  const handleReplyToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmailConfig({
      ...emailConfig,
      replyToAddress: value,
    });

    // Immediate email validation
    if (setInvalidFields) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const fieldName = 'emails-email-replyToAddress';

      setInvalidFields((prev) => {
        const newInvalidFields = new Set(prev);

        // If email is empty or invalid, add to invalid fields
        if (value && !emailRegex.test(value)) {
          newInvalidFields.add(fieldName);
        } else {
          // If email is valid, remove from invalid fields
          newInvalidFields.delete(fieldName);
        }

        return newInvalidFields;
      });
    }
  };

  const handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailConfig({
      ...emailConfig,
      subject: e.target.value,
    });
  };

  const handleCustomMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEmailConfig({
      ...emailConfig,
      customMessage: e.target.value,
    });
  };

  return (
    <div className="space-y-6">
      {/* Email Configuration Header */}
      {props.showSectionHeader !== false && (
        <div className="box-border flex h-[30px] items-center justify-between rounded-[6px] border border-edge bg-paper-inset p-[8px]">
          <span className="font-inter text-base font-medium text-ink-secondary">
            Email Configuration
          </span>
        </div>
      )}

      <div className="space-y-4">
        {/* Enable Custom Email Toggle */}
        <div className="flex items-center space-x-2">
          <Switch checked={emailConfig.enableCustomEmail} onCheckedChange={handleToggleChange} />
          <Label>Enable Custom Email</Label>
        </div>

        {/* Custom Email Form - Only show when toggle is enabled */}
        {emailConfig.enableCustomEmail && (
          <div className="flex w-full flex-col gap-2 rounded-md border border-edge bg-paper-inset p-2">
            {/* Reply-To Address */}
            <div className="flex flex-col">
              <Label className="text-base font-medium leading-6 text-ink-secondary">
                Reply-To Address
              </Label>
              <FormInput
                id="reply-to-address"
                type="email"
                value={emailConfig.replyToAddress}
                onChange={handleReplyToChange}
                onFocus={() => handleFieldFocus?.('emails-email-replyToAddress')}
                placeholder="Enter reply-to email address"
                className={`h-7 ${
                  isFieldInvalid?.('emails-email-replyToAddress')
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                    : ''
                }`}
              />
            </div>

            {/* Subject */}
            <div className="flex flex-col">
              <Label className="text-base font-medium leading-6 text-ink-secondary">Subject</Label>
              <FormInput
                id="subject"
                type="text"
                value={emailConfig.subject}
                onChange={handleSubjectChange}
                onFocus={() => handleFieldFocus?.('emails-email-subject')}
                placeholder="Enter email subject"
                className={`h-7 ${
                  isFieldInvalid?.('emails-email-subject')
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                    : ''
                }`}
              />
            </div>

            {/* Custom Message */}
            <div className="flex flex-col">
              <div className="flex items-center mb-1 justify-between">
                <Label className="text-base font-medium leading-6 text-ink-secondary">
                  Custom Message
                </Label>
                <FormSelect
                  className="h-8 text-xs text-ink-secondary focus:ring-2 focus:ring-sage focus:border-sage"
                  defaultValue=""
                  onChange={handleVariableSelect}
                >
                  <option value="" disabled>
                    Insert Variable
                  </option>
                  {emailVariables.map((variable) => (
                    <option key={variable.value} value={variable.value}>
                      {variable.label}
                    </option>
                  ))}
                </FormSelect>
              </div>
              <FormTextarea
                ref={textareaRef}
                id="custom-message"
                value={emailConfig.customMessage}
                onChange={handleCustomMessageChange}
                onBlur={handleTextareaBlur}
                onFocus={() => handleFieldFocus?.('emails-email-customMessage')}
                placeholder="Enter your custom message here..."
                className={`h-[140px] resize-none ${
                  isFieldInvalid?.('emails-email-customMessage')
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                    : ''
                }`}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EmailConfigurationTab;
